import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  fetchInvoices,
  isPayableOpenInvoice,
  pastDueInvoicesQueryKey,
} from 'src/models/payment/payment';
import { formatCurrency } from 'src/utils/numberFormatter';

import { BlockingReasonFallback, BlockingReasonRow } from './BlockingReasonRow';
import {
  BlockingReasonAction,
  BlockingReasonVariant,
  CheckinBlocker,
} from './_types';
import { blockingReasonTranslationKey } from './blockingReasonTranslationKey';

export function PaymentRequiredReason({
  actions,
  userId,
  resolved = false,
  resolvedBy,
  variant = 'checkin',
}: {
  actions?: BlockingReasonAction[];
  userId: string;
  resolved?: boolean;
  resolvedBy?: CheckinBlocker['resolvedBy'];
  variant?: BlockingReasonVariant;
}) {
  const { t } = useTranslation('translation', { keyPrefix: 'checkinBlocker' });

  const { data: invoicesPayload, isLoading: isLoadingInvoices } = useQuery({
    queryKey: pastDueInvoicesQueryKey(userId),
    queryFn: () =>
      fetchInvoices({
        userId,
        per: 100,
        filters: { pastDue: true },
      }),
  });

  const invoices = invoicesPayload?.invoices ?? [];

  const payableInvoices = invoices.filter(isPayableOpenInvoice);

  const totalAmountDue = payableInvoices.reduce(
    (acc, invoice) => acc + (invoice.amountDue ?? 0),
    0
  );

  if (isLoadingInvoices && !resolved) {
    if (actions?.length) {
      return (
        <BlockingReasonRow
          title={t(
            blockingReasonTranslationKey(variant, 'paymentRequired', 'titleNoAmount')
          )}
          message={t(
            blockingReasonTranslationKey(variant, 'paymentRequired', 'message')
          )}
          actions={actions}
          color="error"
        />
      );
    }

    return <BlockingReasonFallback />;
  }

  if (resolved && resolvedBy === 'payment') {
    return (
      <BlockingReasonRow
        message={t(
          blockingReasonTranslationKey(
            variant,
            'paymentRequired',
            'messagePaid'
          )
        )}
        color="success"
        iconVariant="approved"
      />
    );
  }

  if (resolved && resolvedBy === 'override') {
    return (
      <BlockingReasonRow
        message={t(
          blockingReasonTranslationKey(
            variant,
            'paymentRequired',
            'messageOverridden'
          )
        )}
        color="error"
        iconVariant="approved"
      />
    );
  }

  return (
    <BlockingReasonRow
      title={
        totalAmountDue > 0
          ? t(
              blockingReasonTranslationKey(variant, 'paymentRequired', 'title'),
              {
                amount: formatCurrency(totalAmountDue),
              }
            )
          : t(
              blockingReasonTranslationKey(
                variant,
                'paymentRequired',
                'titleNoAmount'
              )
            )
      }
      message={t(
        blockingReasonTranslationKey(variant, 'paymentRequired', 'message')
      )}
      actions={resolved ? undefined : actions}
      color={resolved ? 'success' : 'error'}
    />
  );
}
